import { useAtomValue } from 'jotai';

import type {
  THygieneTaskCode,
  THygieneTaskType,
  TTreatmentType,
} from '@/react-query/insights/types';
import { usePreferences } from '@/react-query/usePreferences';

import { isCountsAtom } from '../atoms';
import { TASK_CODE_TO_TYPE } from '../types';

import { useGetFilteredClinicalData } from './data/getClinicalData';
import { useGetFilteredHygieneData } from './data/getHygieneData';
import { taskCodesToTypes } from './taskTypeHelpers';

export const useGetAvailableTreatments = (
  practices?: string[],
  providers?: string[],
) => {
  const { data: filteredData } = useGetFilteredClinicalData({
    isTreatmentsEnabled: false,
    isMaterialityEnabled: false,
    isProviderRoleEnabled: false,
    isPracticesEnabled: false,
  });

  const { data: prefs } = usePreferences();

  const enabledRecos = prefs?.insightsEnabledRecos ?? [];

  const availableTreatments = filteredData?.practices
    ?.filter(
      (practice) => !practices || practices.includes(practice.practiceVideaId),
    )
    ?.flatMap((practice) =>
      practice.providers
        ?.filter(
          (provider) =>
            !providers ||
            providers.includes(
              provider.providerVideaId ?? provider.providerId.toString(),
            ),
        )
        ?.flatMap((provider) => {
          const productionTx = Object.entries(provider.production ?? {})
            .filter(([, value]) => value?.current?.count)
            .map(([tx]) => tx);
          const recommendationsTx = Object.entries(
            provider.recommendations ?? {},
          )
            .filter(([, value]) => value?.current?.total?.treatmentCount)
            .map(([tx]) => tx);
          const availableTx = [...productionTx, ...recommendationsTx];
          return availableTx;
        }),
    )
    ?.filter((tx) => tx !== undefined)
    ?.reduce((acc, tx) => {
      if (
        !acc.includes(tx as TTreatmentType) &&
        enabledRecos.includes(tx as TTreatmentType)
      ) {
        acc.push(tx as TTreatmentType);
      }
      return acc;
    }, [] as TTreatmentType[]);

  return availableTreatments ?? [];
};

export const useGetAvailableTasks = (
  practices?: string[],
  providers?: string[],
) => {
  const { filteredData } = useGetFilteredHygieneData();
  const { data: prefs } = usePreferences();
  const isCounts = useAtomValue(isCountsAtom);

  const enabledTasks = prefs?.insightsEnabledTasks ?? [];

  const availableTasks = filteredData?.practices
    ?.filter(
      (practice) => !practices || practices.includes(practice.practiceVideaId),
    )
    ?.flatMap((practice) =>
      practice.hygienists
        ?.filter(
          (hygienist) =>
            !providers ||
            providers.includes(
              hygienist.providerVideaId ?? hygienist.providerId.toString(),
            ),
        )
        ?.flatMap((hygienist) => {
          const tasks = Object.entries(hygienist.tasks ?? {})
            .filter(([taskCode, taskData]) => {
              // Filter out tasks that have no completed value or suggested value based on if we're are showing $ or counts
              const task = TASK_CODE_TO_TYPE[taskCode];
              const taskValue = !isCounts
                ? practice.estimatedHygieneValues[task]?.value
                : 1;

              return (
                (isCounts && taskData?.current?.tasksCompleted) ||
                (!isCounts && taskData?.current?.tasksCompletedValue) ||
                taskData?.current?.tasksSuggested * taskValue
              );
            })
            .map(([taskCode]) => taskCode);

          return taskCodesToTypes(tasks as THygieneTaskCode[]);
        }),
    )
    ?.filter((task) => task !== undefined)
    ?.reduce((acc, task) => {
      if (!acc.includes(task) && enabledTasks.includes(task)) {
        acc.push(task);
      }
      return acc;
    }, [] as THygieneTaskType[]);

  return availableTasks ?? [];
};
